import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Ava } from '@curry-group/mui-curcuma';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMessageBot } from '@fortawesome/pro-light-svg-icons';
import { Message } from '..';
import { assetUrl, getMessageRoute, resolveTimestamp } from '../../../../helper';
import { IMessageModel, getMessageCreatedByName } from '../../../../model/communication/Message';
import { MessageThreadDivider } from '../divider';
import { buildAbbreviation } from '../../../../helper/user';
import { ThingTypes, transformForList } from '../../../../model/ryve/Thing';
import dayjs from 'dayjs';
import { IListItem } from '../../../../model/list/ListItem';
import { alertMessage } from '../../../../data/reducer/userprofile';
import { IDetailItem } from '../../../../model/detail';
import { BOT_CREATED_BY } from '../../../../data/reducer/botChat';

export interface IMessageWrapperProps {
  currentUserId: string;
  message: IMessageModel;
  allowThreading?: boolean;
  width?: number;
  asInlineThread?: boolean;
  inlineThreading?: boolean;
  allowVoting?: boolean;
  allowQuotes?: boolean;
  manageMessages?: boolean;
  /** Bot conversation — see IMessagesProps.isBotChat. */
  isBotChat?: boolean;
  threadingClicked?: (message: IMessageModel) => void;
  voteUpClick?: (message: IMessageModel) => void;
  voteDownClick?: (message: IMessageModel) => void;
  quoteClicked?: (message: IMessageModel) => void;
  emoteClicked?: (message: IMessageModel, emoji: string) => void;
  shareClicked?: (message: IMessageModel) => void;
  loadThreadMessages?: (message: IMessageModel) => void;
  editMessage?: (message: IMessageModel) => void;
  deleteMessage?: (message: IMessageModel) => void;
  onMessageRead?: (message: IMessageModel) => void;
  onJoinConference?: (message: IMessageModel) => void;
  onReferenceSelected?: (type: string, data: any) => void;
  detailItem?: IDetailItem;
  activeParticipants?: any;
  isPublic: boolean;
  isListed: boolean;
}

export const MessageWrapper: React.FC<IMessageWrapperProps> = ({
  currentUserId,
  inlineThreading,
  message,
  allowVoting,
  asInlineThread,
  allowThreading,
  width,
  allowQuotes,
  manageMessages,
  isBotChat,
  voteUpClick,
  voteDownClick,
  threadingClicked,
  quoteClicked,
  emoteClicked,
  loadThreadMessages,
  editMessage,
  deleteMessage,
  onMessageRead,
  onJoinConference,
  onReferenceSelected,
  activeParticipants,
  shareClicked,
  isPublic,
  isListed,
  detailItem
}) => {
  const dispatch = useDispatch();
  const state = useSelector(state => state.communication?.messages?.[message.id]);
  const detailType = useSelector(state => state.detail?.item?.type);
  // Saved bot chats are immutable: edit/delete must be hidden for both user and
  // bot turns regardless of permissions. Detect via the same flag the backend
  // sets on the Thing (with title-suffix fallback for older saves).
  const detailIsBotChat = useSelector(state => {
    const c: any = state.detail?.item?.content;
    if (c?.isBotChat === true) return true;
    if (typeof c?.title === 'string' && c.title.endsWith(' - Bot')) return true;
    return false;
  });
  // A fresh bot session has no detail item, so the detail-based detection alone
  // let the edit menu slip through there. The explicit prop from the bot views
  // is authoritative; the detail check stays as a fallback for saved chats
  // rendered through the generic detail loader.
  const botConversation = !!isBotChat || detailIsBotChat;
  const isOneToOne = detailType === ThingTypes.OneToOne;
  const isOwnMessage = message.createdBy === currentUserId;
  let userVoting = 0;
  if (message.voting) {
    userVoting = message.voting[currentUserId];
  }
  const modified = message.createdAt !== message.modifiedAt;

  const result: any = [];
  const loadedThreadChildren = state?.messages;
  const numLoadedThreadChildren = loadedThreadChildren?.length;
  let newestAnswer: IMessageModel | null;
  if (!!loadedThreadChildren && numLoadedThreadChildren && numLoadedThreadChildren > 0) {
    newestAnswer = loadedThreadChildren[numLoadedThreadChildren - 1];
  } else {
    newestAnswer = null;
  }

  const isBotMessage = message.createdBy === BOT_CREATED_BY;
  const botAvatarNode = isBotMessage ? (
    <Ava size="small" src={undefined} style={{ color: '#1565c0', background: '#e3f2fd', fontWeight: 500 }}>
      <FontAwesomeIcon icon={faMessageBot} />
    </Ava>
  ) : undefined;

  // Bot chats are read-only — never expose edit or delete affordances.
  const canEdit = !botConversation && (isOwnMessage || !!manageMessages);
  const canDelete = !botConversation && message.numThreadChildren === 0 && (isOwnMessage || !!manageMessages);

  result.push(
    <Message
      markdown={isBotMessage}
      allowQuotes={allowQuotes && !botConversation}
      allowVoting={allowVoting && !botConversation}
      allowThreading={allowThreading && !botConversation}
      asInlineThread={asInlineThread}
      canEdit={canEdit}
      canDelete={canDelete}
      key={'message-' + message.id}
      width={width}
      parentId={message.parent}
      id={message.id}
      createdBy={message.createdBy}
      isOwnMessage={isOwnMessage}
      deleted={message.deleted}
      modified={modified}
      content={message.content}
      quotes={message.quotesResolved}
      numVotes={message.numVotes}
      attachments={message.attachmentsResolved}
      attachedThing={message.attachedThingResolved}
      userVoting={userVoting}
      numThreadMessages={inlineThreading ? 0 : message.numThreadChildren}
      references={message.referencesResolved}
      timestamp={modified ? resolveTimestamp(message.modifiedAt) : resolveTimestamp(message.createdAt)}
      abbreviation={buildAbbreviation(getMessageCreatedByName(message, ''))}
      avatarSrc={message.createdByResolved?.content?.avatar ? assetUrl(message.createdByResolved?.content?.avatar, true) : undefined}
      avatarNode={botAvatarNode}
      userStatus={message.createdByResolved?.content?.generalUserStatus ?? ''}
      name={isBotMessage ? 'Medtec Online-Bot' : getMessageCreatedByName(message)}
      conferenceInfo={message.conferenceInfo}
      voteDownClick={() => voteDownClick?.(message)}
      voteUpClick={() => voteUpClick?.(message)}
      onEditClicked={() => editMessage?.(message)}
      onDeleteClicked={() => deleteMessage?.(message)}
      quoteClicked={() => quoteClicked?.(message)}
      emoteClicked={(emoji:string) => emoteClicked?.(message, emoji)}
      onMessageRead={() => onMessageRead?.(message)}
      onJoinConference={m => onJoinConference?.(m || message)}
      threadingClicked={() => threadingClicked?.(message)}
      onReferenceSelected={onReferenceSelected}
      emotes={message.emotes}
      activeParticipants={activeParticipants}
      newestAnswer={newestAnswer}
      shareAllowed={shareClicked !== undefined && isListed && !isOneToOne}
      shareClicked={() => shareClicked?.(message)}
      alertClicked={(alertType: string, alertContent: string) => {
        let transformedItems = !!detailItem
          ? transformForList([detailItem].map(item => {
            return {
              _id: item._id,
              _score: 0,
              _source: {
                __original: item,
                type: item.type,
                content: {...item.content } as any,
                created: dayjs(item.modifiedAt).unix() * 1000,
                categories: item.categories,
                _id: item._id,
                _score: 0,
              }
            };}))
          : [];
        let resolvedItem: IListItem;
        if (transformedItems.length > 0) {
          resolvedItem = transformedItems[0];
        } else {
          return;
        }
        let detailLink = `${window.location.href}`;
        if (!!detailLink) {
          const detailRoute = detailLink.split('?')[0];
          detailLink = `${detailRoute}?${getMessageRoute(message, resolvedItem)}`;
        }
        if (!!detailLink) {
          dispatch(alertMessage({
            userId: currentUserId,
            accusedId: message.createdBy,
            alertType: alertType,
            alertMessage: alertContent,
            messageId: message.id,
            messageLink: detailLink,
          }));
        }
      }}
    />
  );

  if (inlineThreading && message.numThreadChildren && loadedThreadChildren && numLoadedThreadChildren) {
    const numThreadChildren = message.numThreadChildren - numLoadedThreadChildren;
    result.push(
      <MessageThreadDivider
        key={message.id}
        caption={!!numThreadChildren ? numThreadChildren + ' weitere Antworten' : undefined}
        working={state?.working || state?.olderWorking}
        loadMoreClicked={() => {
          loadThreadMessages?.(message);
        }}
      />
    );

    for (let threadIndex = 0; threadIndex < numLoadedThreadChildren; threadIndex++) {
      const threadMessage = loadedThreadChildren[threadIndex];
      result.push(
        <MessageWrapper
          currentUserId={currentUserId}
          allowQuotes={allowQuotes}
          quoteClicked={() => quoteClicked?.(threadMessage)}
          allowVoting={allowVoting}
          voteUpClick={() => voteUpClick?.(threadMessage)}
          voteDownClick={() => voteDownClick?.(threadMessage)}
          inlineThreading={inlineThreading}
          asInlineThread={true}
          allowThreading={false}
          width={width}
          key={'thread-message-' + threadMessage.id}
          message={threadMessage}
          onReferenceSelected={onReferenceSelected}
          isPublic={isPublic}
          isListed={isListed}
        />
      );
      if (threadIndex !== numLoadedThreadChildren - 1) {
        result.push(<MessageThreadDivider key={'thread-divider-' + threadMessage.id} />);
      }
    }
  }

  return <>{result}</>;
};
